import { FormEvent, useEffect, useRef, useState } from 'react'

import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { checkCityIataCodes } from 'entities/ticket/lib/getAgeForInfants'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import findTicketStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import { FormPassengersValues } from 'entities/ticket/models/form-passengers'
import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import { TYPE_TIMER } from 'entities/ticket/models/timer'
import timerFormStore from 'entities/ticket/models/timer/timer-store'
import { PurchaseCloseCircle } from 'entities/ticket/ui/purchase-close-circle'
import { PurchaseForm } from 'entities/ticket/ui/purchase-from'
import {
  getValidationError,
  getValidationErrorContact,
} from 'entities/ticket/ui/purchase-from/lib/getValidationError.ts'
import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import {
  DomesticAirLines,
  ID_AVAILABLE_COUNTRIES_ADULT,
  TYPE_AGE,
} from 'shared/constants'
import { EMAIL_REGEX, REGEX_EMAIL } from 'shared/constants/regex'
import error_icon from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText.ts'
import { priceShow } from 'shared/lib/priceShow'
import citizenshipStore from 'shared/store/citizenship-store'
import serialDocumentStore from 'shared/store/serial-document-store.ts'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { InputWithLabel } from 'shared/ui/input-label'
import MBonusLine from 'shared/ui/mbonus/mbonus-line/mbonus-line'
import { Paper } from 'shared/ui/paper'
import { customValidatePhone } from 'shared/ui/phone-input-label'
import { PhoneInputWhitLabel } from 'shared/ui/phone-input-label/phone-input-label'
import { PopupContent } from 'shared/ui/poup-content'

// import ConfirmFrom from '../../components/Confirm'
import HeaderPurchase from '../../components/Header'
import { PopUpPurchase } from '../../components/PopPurchase'
import { getIdPassportAvailable } from '../../lib/getAvailableCountries'

import { ExitBookingModal } from './ui/exit-booking-modal'

// import { TYPE_TEXT } from '../../model'
import {
  prepareDataForRequestBookTicket,
  prepareFormPurchase,
} from './lib/prepareForm'
import s from './styles.module.scss'

export const FormPurchase = observer(() => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const {
    adult,
    child,
    infant,
    // infants_with_place
  } = findTicketFormStore.getCountPassengers()

  const [loading, setLoading] = useState(false)

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const selectedTicket = listTicketsStore.getSelectedTicket()

  const timerData = timerFormStore.getTimerData(TYPE_TIMER.FORM_PURCHASE)

  const isAvailableCountries = getIdPassportAvailable(
    selectedTicket,
    ID_AVAILABLE_COUNTRIES_ADULT,
  )

  const methods = useForm<FormPassengersValues>({
    defaultValues: prepareFormPurchase({
      purchasePassengerData: formPassengersStore?.formPassengersStore,
      userData: userStore.account,
      forMySelf: findTicketFormStore.formFindTicket.forMySelf,
      isAvailableCountries: isAvailableCountries,
      search: {
        adult,
        child,
        infant,
      },
    }),
  })

  const isLeavingRef = useRef(false)

  const { info } = serialDocumentStore

  const getPassportInfo = (citizenship: string, documentType: string) => {
    const citizenshipData = info?.find(
      (item) => item.citizenship === citizenship,
    )

    if (!citizenshipData) return undefined

    const documentData = citizenshipData?.data.find(
      (doc) => doc.document_type === documentType,
    )

    return documentData || undefined
  }

  const exitPurchaseForm = () => {
    isLeavingRef.current = true
    closeDialog()
    formPassengersStore.clearValuesForm()
    navigate(-1)
  }

  const showExitConfirmation = () => {
    openDialog({
      isDialog: false,
      isClickable: false,
      borderRadius: 20,
      padding: [20],
      child: (
        <ExitBookingModal
          onContinue={closeDialog}
          onExit={exitPurchaseForm}
        />
      ),
    })
  }

  const requestExit = () => {
    if (
      findTicketFormStore.formFindTicket.forMySelf ||
      formPassengersStore.hasFormChanges(methods.getValues())
    ) {
      showExitConfirmation()

      return
    }

    exitPurchaseForm()
  }

  useEffect(() => {
    formPassengersStore.initializeFormSnapshot(methods.getValues())

    return () => {
      if (isLeavingRef.current) return

      const passengerForm = methods.getValues()

      passengerForm.confirm = false

      formPassengersStore.setValuesForm(passengerForm)
    }
  }, [])

  const onSubmit = async (data: FormPassengersValues) => {
    setLoading(true)
    if (!timerData?.isRunning) {
      openDialog({
        child: <PopUpPurchase onClick={onGoBack} />,
        isDialog: false,
      })
    } else {
      try {
        const preparedData = prepareDataForRequestBookTicket({
          data: data,
          selectedTicket: selectedTicket,
          ticketInsurances: formPassengersStore.selectedInsurances,
        })

        const { data: validation } =
          await BuyTicketApi.getValidation(preparedData)

        const isSuccess = validation.status === 'success'
        const isError = validation.status === 'error'
        const response = validation?.data

        if (isSuccess) {
          navigate(RoutingPaths.avia.purchase.check.path())
        } else if (isError && response.passengers) {
          const firstError = getValidationError(response, methods.setError)

          if (firstError) methods.setFocus(firstError)
        } else if (isError && !response.passengers) {
          getValidationErrorContact(response, methods.setError)
        } else if (isError && !response) {
          openDialog({
            isDialog: false,
            child: (
              <PopupContent
                image={error_icon}
                button={t('close')}
                onClick={closeDialog}
                title={t('error_popup')}
                description={validation?.message}
              />
            ),
          })
        }
      } catch (err) {
        const errorText = getErrorMessage(err)

        openDialog({
          isDialog: false,
          child: (
            <PopupContent
              image={error_icon}
              button={t('close')}
              onClick={closeDialog}
              title={t('error_popup')}
              description={errorText || t('error_popup_description')}
            />
          ),
        })
      } finally {
        setLoading(false)
      }
    }
  }

  const onGoBack = () => {
    navigate(RoutingPaths.avia.search.path())

    closeDialog()
  }

  const onChangeInputEmail = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value?.replace(REGEX_EMAIL, '')
  }

  const listOptionsCountry = citizenshipStore.citizenshipAvia?.map((item) => ({
    value: item?.code,
    name: item?.country,
  }))

  let startCount = 0

  const { errors } = methods.formState

  const finalPrice = selectedTicket?.price.KGS

  // const isConfirmed = useWatch({ control: methods.control, name: 'confirm' })

  const { age } = checkCityIataCodes(findTicketStore.formFindTicket.directions)

  const totalPrice = (
    Number(finalPrice) + Number(formPassengersStore.calculateInsurancePrice())
  ).toString()

  const insuranceText = formPassengersStore.selectedInsurances
    ?.map((insurance) => insurance.name)
    .join(', ')

  const insurancePrice = formPassengersStore.selectedInsurances?.reduce(
    (num, insurance) => num + parseInt(insurance?.price || '0'),
    0,
  )

  return (
    <div className={s.container}>
      <div className={s.header}>
        <HeaderPurchase
          title={t('purchase_page.title')}
          onGoBack={requestExit}
        />
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {new Array(adult)?.fill('').map((_, indexAdult) => {
            startCount++

            const isAvailableCountriesAdult = getIdPassportAvailable(
              selectedTicket,
              ID_AVAILABLE_COUNTRIES_ADULT,
            )

            return (
              <div
                className={s.form}
                key={indexAdult}
              >
                <PurchaseCloseCircle
                  keyPassenger={{ age: TYPE_AGE.ADULTS, index: indexAdult }}
                  isAvailableCountries={isAvailableCountriesAdult}
                  info={{
                    count: startCount,
                    subText: t('purchase_page.adults'),
                  }}
                >
                  <PurchaseForm
                    formInformation={{
                      citizenship: listOptionsCountry,
                      isAvailableCountries: isAvailableCountriesAdult,
                      keyPassenger: { age: TYPE_AGE.ADULTS, index: indexAdult },
                    }}
                    getPassportInfo={getPassportInfo}
                  />
                </PurchaseCloseCircle>
              </div>
            )
          })}

          {new Array(child)?.fill('').map((_, indexChild) => {
            startCount++

            const isAvailableCountriesChild = getIdPassportAvailable(
              selectedTicket,
              DomesticAirLines,
            )

            return (
              <div
                className={s.form}
                key={indexChild}
              >
                <PurchaseCloseCircle
                  key={indexChild}
                  keyPassenger={{
                    age: TYPE_AGE.CHILDREN,
                    index: indexChild,
                  }}
                  isAvailableCountries={isAvailableCountriesChild}
                  info={{
                    count: startCount,
                    subText: t('purchase_page.children', { age }),
                  }}
                >
                  <PurchaseForm
                    formInformation={{
                      citizenship: listOptionsCountry,
                      isAvailableCountries: isAvailableCountriesChild,
                      keyPassenger: {
                        age: TYPE_AGE.CHILDREN,
                        index: indexChild,
                      },
                    }}
                    getPassportInfo={getPassportInfo}
                  />
                </PurchaseCloseCircle>
              </div>
            )
          })}

          {/* {new Array(infants_with_place)?.fill('').map((_, indexInfantSeat) => {
            startCount++

            const isAvailableCountries = getIdPassportAvailable(
              selectedTicket,
              ID_AVAILABLE_COUNTRIES_ADULT,
            )

            return (
              <div
                className={s.form}
                key={indexInfantSeat}
              >
                <PurchaseCloseCircle
                  key={indexInfantSeat}
                  keyPassenger={{
                    age: TYPE_AGE.INFANTS_WITH_SEAT,
                    index: indexInfantSeat,
                  }}
                  isAvailableCountries={isAvailableCountries}
                  info={{
                    count: startCount,
                    subText: t('purchase_page.infants'),
                  }}
                >
                  <PurchaseForm
                    formInformation={{
                      citizenship: listOptionsCountry,
                      isAvailableCountries: isAvailableCountries,
                      keyPassenger: {
                        age: TYPE_AGE.INFANTS_WITH_SEAT,
                        index: indexInfantSeat,
                      },
                    }}
                    getPassportInfo={getPassportInfo}
                  />
                </PurchaseCloseCircle>
              </div>
            )
          })} */}

          {new Array(infant)?.fill('').map((_, indexInfant) => {
            startCount++
            const isAvailableCountriesInfants = getIdPassportAvailable(
              selectedTicket,
              DomesticAirLines,
            )

            return (
              <div
                className={s.form}
                key={indexInfant}
              >
                <PurchaseCloseCircle
                  key={indexInfant}
                  keyPassenger={{
                    age: TYPE_AGE.INFANTS_WITHOUT_SEAT,
                    index: indexInfant,
                  }}
                  isAvailableCountries={isAvailableCountriesInfants}
                  info={{
                    count: startCount,
                    subText: t('purchase_page.infants', { age }),
                  }}
                >
                  <PurchaseForm
                    formInformation={{
                      citizenship: listOptionsCountry,
                      isAvailableCountries: isAvailableCountriesInfants,
                      keyPassenger: {
                        age: TYPE_AGE.INFANTS_WITHOUT_SEAT,
                        index: indexInfant,
                      },
                    }}
                    getPassportInfo={getPassportInfo}
                  />
                </PurchaseCloseCircle>
              </div>
            )
          })}

          <div className={s.contact}>
            <Paper padding={[20, 16]}>
              <p className={s.title}>{t('purchase_page.contact_title')}</p>

              <div>
                <Controller
                  name="phone_number"
                  control={methods.control}
                  rules={{
                    required: true,
                    validate: customValidatePhone,
                  }}
                  render={({ field }) => (
                    <PhoneInputWhitLabel
                      ref={field.ref}
                      value={field?.value}
                      error={errors.phone_number}
                      label={t('purchase_page.phone_number')}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
              </div>

              <div className={s.itemForm}>
                <Controller
                  name="email"
                  control={methods.control}
                  rules={{
                    pattern: {
                      value: EMAIL_REGEX,
                      message: '',
                    },
                    // required: true,
                  }}
                  render={({ field }) => (
                    <InputWithLabel
                      lang={'en'}
                      ref={field.ref}
                      value={field.value}
                      error={errors?.email}
                      label={t('email')}
                      style={{ fontWeight: 500 }}
                      onChange={(event) =>
                        field.onChange(onChangeInputEmail(event))
                      }
                    />
                  )}
                />
              </div>
            </Paper>
          </div>

          <div className={s.destination}>
            <Paper padding={[20, 16]}>
              <div className={s.information}>
                <div className={s.flexxer}>
                  <p className={clsx('regular-14', s.flexxerTitle)}>
                    {t('purchase_page.price_for_passengers', {
                      count: startCount,
                    })}
                  </p>

                  <p className={'bold-14'}>
                    {priceShow(String(finalPrice || 0))} &#8384;
                  </p>
                </div>

                {!!formPassengersStore.selectedInsurances?.length && (
                  <div className={s.flexxer}>
                    <p className={clsx('regular-14', s.flexxerTitle)}>
                      {t('insurance_info.insurance_price', {
                        text: insuranceText,
                      })}
                    </p>

                    <p className={'bold-14'}>
                      {priceShow(String(insurancePrice))} &#8384;
                    </p>
                  </div>
                )}

                <MBonusLine price={selectedTicket?.bonus_amount} />
              </div>
              <div className={s.flexxerTotalPrice}>
                <p className={clsx('regular-16', s.flexxerTitle)}>
                  {t('total')}
                </p>

                <p className={'bold-18'}>{priceShow(totalPrice)} &#8384;</p>
              </div>

              {/* 
              <Controller
                name={'confirm'}
                control={methods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <ConfirmFrom
                    isChecked={field.value}
                    errors={errors?.confirm}
                    onShowConform={onShowConform}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              /> 
              */}

              {/* !isConfirmed || */}
              <Button disabled={loading}>
                <span className={'semi-bold-16'}>{t('continue')}</span>
              </Button>
            </Paper>
          </div>
        </form>
      </FormProvider>
    </div>
  )
})
